import { FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Popconfirm } from "antd";
import { clsx } from "clsx";
import { Button, Card, Dropdown } from "../../design-system";
import { VendorLogo } from "../project/components/VendorLogo";
// The same card as the project page's header, so the two read alike.
import cardStyles from "../project/components/ProjectCard.module.less";
import { deleteVendor } from "../../common/api/VendorClient";
import { DeleteVendorResponse } from "../../types/VendorTypes";
import { isSuperAdmin } from "../../types/UserGroup";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { getFullUrl } from "../../common/Url";
import { buyerBriefRefPath } from "../../common/BuyerBriefLink";
import { SvgIcon } from "../../common/components/SvgIcon";
import { VendorUpdateModal } from "./components/VendorUpdateModal";
import { VendorDeleteModal } from "./components/VendorDeleteModal";
import { VendorDealsTable } from "./components/VendorDealsTable";
import { VendorPage } from "./VendorPage";
import { useVendorSpend } from "./list/useVendorSpend";
import { vendorTree } from "./list/vendorSpend";
import { AverageScoreCell, SavingsCell, TotalValueCell } from "./list/components/SpendCells";
import classes from "./VendorDetails.module.less";

/**
 * One vendor: who they are, how much business we do with them, and every
 * deal. Built from the same spend model as the vendor list.
 */
export const VendorDetails: FC = () => {
    const { me } = useUserState();
    const navigate = useNavigate();
    const { vendorKey } = useParams();
    const { model, reload } = useVendorSpend();
    const [showUpdate, setShowUpdate] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [relatedData, setRelatedData] = useState<DeleteVendorResponse>();

    // The vendor as a root of the per-vendor tree: its tally over all its deals.
    const node = model && vendorTree(model).find((v) => v.vendor.vendorKey === vendorKey);

    if (!model) return <VendorPage loading />;
    if (!node) {
        return (
            <VendorPage>
                <p className={classes.missing}>There is no vendor with this key.</p>
            </VendorPage>
        );
    }

    const { vendor } = node;
    const supplier = vendor.supplier;
    const briefPath = buyerBriefRefPath(vendor.buyerBrief);
    // The vendor's own URL first; the legacy supplier record may hold one too.
    const website = vendor.url || supplier?.website;
    const logoUrl = vendor.hero || supplier?.logo;
    const admin = isSuperAdmin(me.roles || []);

    const handleDelete = async () => {
        setConfirmDelete(false);
        try {
            const res = await deleteVendor(vendor);
            if (res.orderForms || res.negotiations) setRelatedData(res);
            else navigate("/vendor");
        } catch (error) {
            handleError(error);
        }
    };

    // Super admins edit or delete the vendor from a menu at the card's right.
    const actions = admin && (
        <Popconfirm
            title="Sure to delete?"
            open={confirmDelete}
            onConfirm={handleDelete}
            onCancel={() => setConfirmDelete(false)}
        >
            <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                menu={{
                    items: [
                        { key: "edit", label: "Edit", onClick: () => setShowUpdate(true) },
                        {
                            key: "delete",
                            label: "Delete",
                            danger: true,
                            onClick: () => setConfirmDelete(true),
                        },
                    ],
                }}
            >
                <Button type="text" aria-label="Vendor actions" className={classes.more}>
                    <SvgIcon name="ellipsis" size={16} />
                </Button>
            </Dropdown>
        </Popconfirm>
    );

    // One icon per link, always in the same order; a link the vendor lacks
    // shows its icon greyed out so the row reads alike on every vendor.
    const iconLink = (
        href: string | undefined | false,
        icon: string,
        label: string,
        internal = false
    ) => {
        const content = <SvgIcon name={icon} />;
        if (!href) {
            return (
                <span
                    className={clsx(classes.link, classes["link--missing"])}
                    aria-label={`No ${label.toLowerCase()}`}
                    title={`No ${label.toLowerCase()}`}
                >
                    {content}
                </span>
            );
        }
        return internal ? (
            <Link to={href} className={classes.link} aria-label={label} title={label}>
                {content}
            </Link>
        ) : (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={classes.link}
                aria-label={label}
                title={label}
            >
                {content}
            </a>
        );
    };

    return (
        <VendorPage>
            {showUpdate && (
                <VendorUpdateModal
                    vendor={vendor}
                    onSaved={() => {
                        setShowUpdate(false);
                        reload();
                    }}
                    onCancel={() => setShowUpdate(false)}
                />
            )}
            <VendorDeleteModal
                open={!!relatedData}
                orderForms={relatedData?.orderForms}
                negotiations={relatedData?.negotiations}
                onCancel={() => setRelatedData(undefined)}
                onOk={() => setRelatedData(undefined)}
            />
            <Card className={clsx(cardStyles.headerCard, cardStyles.headerCardWithLogo)}>
                <VendorLogo vendorHero={logoUrl} vendorName={vendor.name} />
                <div className={cardStyles.headerContent}>
                    <div className={cardStyles.fieldRow}>
                        {/* Shares of the row: the name widest, the two short
                            tallies (a count, a score tag) half a money field. */}
                        <div
                            className={clsx(cardStyles.primaryField, cardStyles.fieldUnlabelled)}
                            style={{ flexGrow: 4 }}
                        >
                            <div className={classes.name}>{vendor.name}</div>
                            <div className={classes.links}>
                                {iconLink(website && getFullUrl(website), "global", "Website")}
                                {iconLink(supplier?.linkedIn, "linkedin", "LinkedIn")}
                                {iconLink(supplier?.twitter, "x", "X")}
                                {iconLink(briefPath, "briefs", "Buyer Brief", true)}
                            </div>
                        </div>
                        <div className={cardStyles.fieldFluid} style={{ flexGrow: 1 }}>
                            <div className={cardStyles.fieldLabel}>Deals</div>
                            <div className={clsx(cardStyles.fieldValue, cardStyles.fieldValueCell)}>{node.deals}</div>
                        </div>
                        <div className={cardStyles.fieldFluid} style={{ flexGrow: 1 }}>
                            <div className={cardStyles.fieldLabel}>Avg score</div>
                            <div className={clsx(cardStyles.fieldValue, cardStyles.fieldValueCell)}>
                                <AverageScoreCell tally={node} />
                            </div>
                        </div>
                        <div className={cardStyles.fieldFluid} style={{ flexGrow: 2 }}>
                            <div className={cardStyles.fieldLabel}>Analyzed spend</div>
                            <div className={clsx(cardStyles.fieldValue, cardStyles.fieldValueCell)}>
                                <TotalValueCell tally={node} />
                            </div>
                        </div>
                        <div className={cardStyles.fieldFluid} style={{ flexGrow: 3 }}>
                            <div className={cardStyles.fieldLabel}>Realized savings</div>
                            <div className={clsx(cardStyles.fieldValue, cardStyles.fieldValueCell)}>
                                <SavingsCell tally={node} />
                            </div>
                        </div>
                        <div className={cardStyles.cardMenu}>{actions}</div>
                    </div>
                </div>
            </Card>

            <VendorDealsTable deals={node.children} onUpdate={reload} />
        </VendorPage>
    );
};
